import { Dispatch, SetStateAction, ChangeEvent } from "react";
import InfoTooltip from "../../../../components/Buttons/InfoTooltip";
import ConditionalSelectField from "../../../../components/Form/ConditionSelectField";
import SelectField from "../../../../components/Form/SelectFields";
import { EcleanCuration, unitOptions } from "../GenerateDSComponents/Curate";
import CheckboxField from "../../../../components/Form/CheckboxField";
import BooleanField from "../../../../components/Form/BooleanField";

export default function StandardizeValues({
    showSelect,
    form,
    setForm,
    fieldOptions,
    menuPortalTargetId
}: {
    showSelect: boolean,
    form: EcleanCuration,
    setForm: Dispatch<SetStateAction<EcleanCuration>>,
    fieldOptions: {
        value: string,
        label: string
    }[],
    menuPortalTargetId: string,
}) {

    const handleUnitColumnExists = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;

        if (form.eclean_std_values) {
            setForm({
                ...form,
                eclean_std_values: {
                    ...form.eclean_std_values,
                    units_column: checked ? form.eclean_std_values.units_column : '',
                },
                input_units: checked ? null : 'neglogM',
            })
        }
    }

    const handleUnitsIn = (e: any, name: string) => {
        const value = e.value as string;

        if (form.eclean_std_values) {
            setForm({
                ...form,
                input_units: value,
            })
        }
    }

    const handleSelectUnits = (e:any, name: string) => {
        const value = e.value;

        if (form.eclean_std_values) {
            let valueRange = form.eclean_std_values.value_range;

            // If user updates Units Out field, set value range field back to default
            // If Units Out value === neglogM, set upper_bound to 12. Otherwise, set to null
            if (name === 'units_out') {
                valueRange = {
                    lower_bound: null,
                    upper_bound: value === 'neglogM' ? 12 : null,
                }
            }

            setForm({
                ...form,
                eclean_std_values: {
                    ...form.eclean_std_values,
                    [name]: value,
                    value_range: valueRange,
                }
            })
        }
    }

    const handleSelectValueOrQualifier = (e:any, name: string) => {
        const value = e.value;

        if (form.eclean_std_values) {
            setForm({
                ...form,
                eclean_std_values: {
                    ...form.eclean_std_values,
                    [name]: value,
                },
                eclean_std_binary: form.eclean_std_binary ? {
                    ...form.eclean_std_binary,
                    [name]: value,
                } : null,
                eclean_std_regression: form.eclean_std_regression ? {
                    ...form.eclean_std_regression,
                    [name]: value,
                } : null,
            })
        }
    }

    const handleDuplicateValues = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        const name = e.target.name;

        if (form.eclean_std_values) {
            if (name === 'kwargs_prune_duplicate_values') {
                setForm({
                    ...form,
                    eclean_std_values: {
                        ...form.eclean_std_values,
                        kwargs_prune_duplicate_values: checked ? {
                            sigfigs: 3
                        } : null,
                    }
                })
            } else if (name === 'kwargs_prune_population_duplicate_values') {
                setForm({
                    ...form,
                    eclean_std_values: {
                        ...form.eclean_std_values,
                        kwargs_prune_population_duplicate_values: checked ? {
                            min_num_duplicates: 10,
                            sigfigs: 5
                        } : null,
                    }
                })
            }
        }
    }

    const handleOutlierValues = (e: ChangeEvent<HTMLInputElement>) => {
        const checked = e.target.checked;
        const name = e.target.name;
        const id = e.target.id;

        if (form.eclean_std_values) {
            if (name === 'kwargs_prune_population_outliers') {
                setForm({
                    ...form,
                    eclean_std_values: {
                        ...form.eclean_std_values,
                        kwargs_prune_population_outliers: {
                            iqr_fraction: 1.5,
                            iqr_min: 2,
                            keep_high_outliers: id === 'high' ? checked : form.eclean_std_values.kwargs_prune_population_outliers.keep_high_outliers,
                            keep_low_outliers: id === 'low' ? checked : form.eclean_std_values.kwargs_prune_population_outliers.keep_low_outliers, 
                        }
                    }
                })
            } else if (name === 'kwargs_prune_duplicate_outliers') {
                setForm({
                    ...form,
                    eclean_std_values: {
                        ...form.eclean_std_values,
                        kwargs_prune_duplicate_outliers: checked ? {
                            iqr_fraction: 1.5,
                            iqr_min: 0.5,
                            keep_high_outliers: false,
                            keep_low_outliers: false,
                        } : null,
                    }
                })
            } else if (name === 'kwargs_prune_iqr_outliers') {
                setForm({
                    ...form,
                    eclean_std_values: {
                        ...form.eclean_std_values,
                        kwargs_prune_iqr_outliers: checked ? {
                            iqr_fraction: 1.5,
                            iqr_min: 0.5,
                            iqr_max: 1,
                        } : null,
                    }
                })
            }
        }
    }

    const handleValueRange = (e: ChangeEvent<HTMLInputElement>) => {
        const name = e.target.name;
        const value = e.target.value;

        if (form.eclean_std_values) {
            let updatedValue: string | number | null = value;

            if (value.trim().length === 0) {
                updatedValue = null;
            } 
            
            setForm({
                ...form,
                eclean_std_values: {
                    ...form.eclean_std_values,
                    value_range: {
                        ...form.eclean_std_values.value_range,
                        [name]: updatedValue,
                    }
                }
            })
        }
    }

    console.log(form)

    return (
        <>
        {form.eclean_std_values && (
            // Continuous only
            <div className="flex flex-col gap-6 justify-start items-start">
                <div className="flex justify-start items-center gap-2">
                    <p className="text-default font-semibold">{form.eclean_std_smiles !== null ? '2' : '1'}. Standardize Values</p>
                    <InfoTooltip 
                        btnWidth="14px"
                        title="Standardize Values"
                        content={
                            <div className="flex flex-col gap-3 text-placeholder text-[12px]">
                                <p className="max-w-[300px]">This pipeline drops missing values, standardizes units and qualifiers, converts units to a common unit, and discards duplicates, values outside the specified range, population outliers, and samples where the spread in values is abnormally high.</p>
                            </div>
                        }
                    />
                </div>
                
                <div className="w-full flex justify-start items-end gap-4">
                    <div className="w-1/2">
                    <BooleanField 
                        label="Dataset contains a units column"
                        id=""
                        name=""
                        isRequired={false}
                        handleChange={handleUnitColumnExists}
                        value={form.input_units === null ? true : false}
                    />
                    </div>
                </div>

                <div className="w-full flex justify-between items-end gap-4">
                    {form.input_units === null ? (
                    <ConditionalSelectField label='Units column' showSelect={showSelect}>
                        <SelectField 
                            label="Units column"
                            id="units_column"
                            name="units_column"
                            value={form.eclean_std_values.units_column ? {
                                value: form.eclean_std_values.units_column,
                                label: form.eclean_std_values.units_column
                            } : null}
                            options={fieldOptions}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleSelectUnits}
                            menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                            />
                    </ConditionalSelectField>
                    ) : (
                        <ConditionalSelectField label='Units In' showSelect={showSelect}>
                            <SelectField 
                                label="Units In"
                                id="units_in"
                                name="units_in"
                                value={form.input_units ? {
                                    value: form.input_units,
                                    label: form.input_units
                                } : null}
                                options={unitOptions}
                                isRequired={true}
                                isCreatable={false}
                                isMulti={false}
                                handleChange={handleUnitsIn}
                                menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                                />
                        </ConditionalSelectField>
                    )}
                    <ConditionalSelectField label='Units out' showSelect={showSelect}>
                        <SelectField 
                            label="Units out"
                            id="units_out"
                            name="units_out"
                            value={form.eclean_std_values.units_out ? {
                                value: form.eclean_std_values.units_out,
                                label: form.eclean_std_values.units_out
                            } : null}
                            options={unitOptions}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleSelectUnits}
                            menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                            />
                    </ConditionalSelectField>
                </div>

                <div className="w-full flex justify-between items-end gap-4">
                    <ConditionalSelectField label='Value Column' showSelect={showSelect}>
                        <SelectField 
                            label="Value Column"
                            id="value_column"
                            name="value_column"
                            value={form.eclean_std_values.value_column ? { 
                                value: form.eclean_std_values.value_column, 
                                label: form.eclean_std_values.value_column 
                            } : null}
                            options={fieldOptions}
                            isRequired={true}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleSelectValueOrQualifier}
                            menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                            />
                    </ConditionalSelectField>
                    <ConditionalSelectField label='Qualifier Column' showSelect={showSelect}>
                        <SelectField 
                            label="Qualifier Column"
                            id="qualifier_column"
                            name="qualifier_column"
                            value={form.eclean_std_values.qualifier_column ? { 
                                value: form.eclean_std_values.qualifier_column, 
                                label: form.eclean_std_values.qualifier_column 
                            } : null}
                            options={fieldOptions}
                            isRequired={false}
                            isCreatable={false}
                            isMulti={false}
                            handleChange={handleSelectValueOrQualifier}
                            menuPortalTarget={menuPortalTargetId ? document.getElementById(menuPortalTargetId) : document.getElementById('curate-ds-popup')}
                            />
                    </ConditionalSelectField>
                </div>

                <p className="text-[14px] font-semibold">Data Pruning</p>

                <div className="w-full flex flex-col gap-4 justify-start items-start">
                    <label className="text-[12px] font-medium">Duplicates</label>
                    <CheckboxField 
                        label="Prune exact duplicates (same structure and value)"
                        id="kwargs_prune_duplicate_values"
                        name='kwargs_prune_duplicate_values'
                        value={form.eclean_std_values.kwargs_prune_duplicate_values === null ? false : true}
                        handleChange={handleDuplicateValues}
                    />
                    <CheckboxField 
                        label="Prune value duplicates"
                        id="kwargs_prune_population_duplicate_values"
                        name='kwargs_prune_population_duplicate_values'
                        value={form.eclean_std_values.kwargs_prune_population_duplicate_values === null ? false : true}
                        handleChange={handleDuplicateValues}
                    />
                </div>

                <div className="w-full flex flex-col gap-4 justify-start items-start">
                    <div className="flex justify-start items-center gap-1">
                        <label className="text-[12px] font-medium">Valid Value Range</label>
                        <InfoTooltip 
                            btnWidth="14px"
                            title="Valid Value Range"
                            content={
                                <div className="flex flex-col gap-3 text-placeholder text-[12px]">
                                    <p className="max-w-[300px]">Values outside the specified range will be discarded. Units for lower bound and upper bound are equal to the value set for <strong>Units out</strong>.</p>
                                </div>
                            }
                        />
                    </div>
                    <div className='w-full flex justify-between items-start gap-4'>
                        <div className="w-1/2">
                            <div className="w-full flex flex-col justify-center items-start gap-2 text-default">
                                <input 
                                    onChange={handleValueRange}
                                    id='lower_bound' 
                                    name='lower_bound' 
                                    type="number"
                                    value={form.eclean_std_values.value_range.lower_bound ?? ''} 
                                    placeholder='Lower Bound' 
                                    className="px-4 py-3 rounded-lg border border-outline focus:border-primary focus:outline-none w-full"
                                />
                                <label htmlFor="lower_bound" className="text-[12px] font-medium">Lower Bound <span className="text-[#7A7A7A]">({form.eclean_std_values.units_out})</span></label>
                            </div>
                        </div>
                        <div className="w-1/2">
                            <div className="w-full flex flex-col justify-center items-start gap-2 text-default">
                                <input 
                                    onChange={handleValueRange}
                                    id='upper_bound' 
                                    name='upper_bound' 
                                    type="number"
                                    value={form.eclean_std_values.value_range.upper_bound ?? ''} 
                                    placeholder='Upper Bound' 
                                    className="px-4 py-3 rounded-lg border border-outline focus:border-primary focus:outline-none w-full"
                                />
                                <label htmlFor="upper_bound" className="text-[12px] font-medium">Upper Bound <span className="text-[#7A7A7A]">({form.eclean_std_values.units_out})</span></label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full flex flex-col gap-4 justify-start items-start">
                    <label className="text-[12px] font-medium">Outliers</label>
                    <CheckboxField 
                        label="Prune high value outliers across the entire dataset"
                        id="high"
                        name='kwargs_prune_population_outliers'
                        value={form.eclean_std_values.kwargs_prune_population_outliers.keep_high_outliers}
                        handleChange={handleOutlierValues}
                    />
                    <CheckboxField 
                        label="Prune low value outliers across the entire dataset"
                        id="low"
                        name='kwargs_prune_population_outliers'
                        value={form.eclean_std_values.kwargs_prune_population_outliers.keep_low_outliers}
                        handleChange={handleOutlierValues}
                    />
                    <CheckboxField 
                        label="Prune value outliers within a group with the same structure"
                        id="kwargs_prune_duplicate_outliers"
                        name='kwargs_prune_duplicate_outliers'
                        value={form.eclean_std_values.kwargs_prune_duplicate_outliers === null ? false : true}
                        handleChange={handleOutlierValues}
                    />
                    <CheckboxField 
                        label="Prune groups with high measurement uncertainty"
                        id="kwargs_prune_iqr_outliers"
                        name='kwargs_prune_iqr_outliers'
                        value={form.eclean_std_values.kwargs_prune_iqr_outliers === null ? false : true}
                        handleChange={handleOutlierValues}
                    />
                </div>
            </div>
        )}
        </>
    )
}